<template>
  <a-modal
    v-model:visible="visible"
    :title="currCate"
    :footer="null"
    :width="597"
    :after-close="afterClose"
    wrap-class-name="modal-top"
  >
    <div class="relative max-h-[610px] min-h-[100px] overflow-auto pt-5">
      <div
        v-if="!currCateItems.length"
        class="absolute left-[235px] top-12 text-lg"
      >
        暂无数据
      </div>
      <div v-else>
        <div class="flex gap-y-6 gap-x-12 flex-wrap">
          <div
            v-for="(item, key) in currCateItems.filter(
              (item) => !item.combination
            )"
            :key="key"
            class="relative w-[244px] h-[315px] rounded-lg border border-solid border-[#8f8f8f]"
            :class="{
              '!border-[#0D84F2]': item.id == selectedId
            }"
            @click="handleSelected(item)"
            @dblclick="submit(item.zip)"
          >
            <div
              class="relative w-full h-[245px] rounded-t-lg flex items-center justify-center border-[2px] border-transparent"
              @mousemove="handleMouseMove(item)"
            >
              <div
                class="border-[2px] border-solid rounded-[7px] border-[#464646]"
                :class="{
                  'h-[195px] pt-10': item.name === '序列帧背景' && showControls
                }"
              >
                <img
                  v-if="item.name !== '序列帧背景'"
                  :src="item.preview"
                  class="max-h-[200px] max-w-[200px]"
                  :class="{
                    'px-[3px] py-[6px]': currCate !== '背景'
                  }"
                />
                <!-- 序列帧背景样式以及交互 -->
                <img
                  v-else
                  :src="framePreview ?? item.preview"
                  class="w-[195px] h-[100px]"
                />
                <div
                  v-if="item.name === '序列帧背景' && showControls"
                  class="ml-1"
                  @click="(e) => e.stopPropagation()"
                >
                  <Icon
                    :icon="
                      animationPlaying ? 'framesPlay.svg' : 'framesStop.svg'
                    "
                    :width="24"
                    :height="24"
                    fill="none"
                    @click="handlePlayClick"
                  />
                  <div
                    @mouseover="() => (sliderHover = true)"
                    @mouseout="() => (sliderHover = false)"
                  >
                    <a-slider
                      v-model:value="framesSliderValue"
                      :tooltip-visible="false"
                      @change="handleFramesChange"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div
              class="w-full h-[70px] bg-[#323232] absolute bottom-0 rounded-b-lg"
            >
              <p class="pt-3 pl-4 text-[18px]">{{ item.name }}</p>
              <div class="opacity-[0.5] pl-4 -mt-4">
                <Icon
                  :icon="item.icons[0]"
                  :width="10"
                  :height="10"
                  class="mb-1"
                />
                <span class="text-[14px] ml-1">
                  {{ item.iconStrs[0] }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="currCateItems.filter((item) => item.combination).length"
          class="mt-10 mb-2 text-lg opacity-[0.5]"
        >
          组合
        </div>
        <div class="flex gap-y-6 gap-x-12 flex-wrap">
          <div
            v-for="(item, key) in currCateItems.filter(
              (item) => item.combination
            )"
            :key="key"
            class="relative w-[248px] h-[315px] rounded-lg border-[0.8px] border-solid border-[#8f8f8f]"
            :class="{
              '!border-[#0D84F2]': item.id == selectedId
            }"
            @click="handleSelected(item)"
            @dblclick="submit(item.zip)"
          >
            <div
              class="relative w-full h-[245px] rounded-t-lg flex items-center justify-center border-[2px] border-transparent"
            >
              <img
                :src="item.preview"
                class="max-h-[200px] max-w-[200px] border-[2px] border-solid rounded-[7px] border-[#464646]"
                :class="{ 'p-1': currCate !== '数据' }"
              />
            </div>

            <div
              class="w-full min-h-[70px] bg-[#323232] absolute bottom-0 rounded-b-lg"
            >
              <p class="pt-3 pl-4 text-[16px]">{{ item.name }}</p>
              <div class="flex ml-4 flex-wrap -mt-4">
                <div
                  v-for="(icon, key2) in item.icons"
                  :key="key2"
                  class="opacity-[0.5]"
                >
                  <Icon
                    :icon="icon"
                    :width="10"
                    :height="10"
                    fill="none"
                    class="mb-[2px]"
                  />
                  <span class="text-[12px] ml-1">
                    {{ item.iconStrs?.[key2] }}
                  </span>
                  <span
                    v-if="key2 !== item.icons?.length - 1"
                    class="-ml-1"
                    :class="{
                      'px-1': item.icons.length <= 4,
                      'px-[1px]': item.icons.length > 4
                    }"
                  >
                    +
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="ml-[148px] mt-5 pb-5">
      <a-button key="back" class="mr-5" @click="() => (visible = false)">
        关闭
      </a-button>
      <a-button type="primary" :disabled="!selectedId" @click="submit()">
        应用
      </a-button>
    </div>
  </a-modal>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { type WatchfaceWidgetCate } from '@/services/widget.service';
import { useWatchfaceWidget, ICON_SRC } from '@/hooks';
import generateID from '@/utils/generate-id';

// 打开或关闭modal
const visible = ref(false);
interface WidgetItem {
  id: string;
  name: string;
  combination: boolean;
  icons: string[];
  iconStrs: string[];
  preview: string;
  zip: string;
}
// 表达式模版
const { watchfaceWidgets, _addWidgetTemplate, downLoadFramesImageArray } =
  useWatchfaceWidget();
const iconMap = new Map([
  ['shape-image.svg', '图片'],
  ['shape-images.svg', '图组'],
  ['shape-group.svg', '组'],
  ['shape-sprite.svg', '动画序列帧'],
  ['shape-text.svg', '文本'],
  ['shape-progress.svg', '进度条']
]);

const framePreview = ref<string>('');
/**序列帧数组 */
const framesArr = ref<string[]>([]);
// const handleFramesResource = async () => {};
const currCate = ref<WatchfaceWidgetCate>();
const currCateItems = computed<WidgetItem[]>(() => {
  const res: WidgetItem[] = [];
  watchfaceWidgets.value
    .filter((item) => item.cate == currCate.value)
    .forEach((item) => {
      const icons = item.shapeTypes
        .map((shapeType) => ICON_SRC[shapeType])
        .filter((icon) => icon !== 'shape-group.svg');
      const iconStrs = item.shapeTypes
        .map((shapeType) => iconMap.get(ICON_SRC[shapeType]))
        .filter((iconStr) => iconStr !== '组');
      res.push({
        id: generateID(),
        name: item.name,
        combination: item.shapeTypes.length > 1,
        icons,
        iconStrs: iconStrs as string[],
        preview: item.preview,
        zip: item.zip
      });
    });

  return res;
});

watch(currCateItems, async () => {
  const _framesData = currCateItems.value;
  if (framesArr.value.length) return;
  for (let item of _framesData) {
    if (item.name == '序列帧背景') {
      framesArr.value = (await downLoadFramesImageArray(item.zip)) as string[];
    }
  }
});

const open = async (_data: { mode: WatchfaceWidgetCate }) => {
  visible.value = true;
  currCate.value = _data.mode;
  framesSliderValue.value = 0;
  handleFramesChange(0);
  animationPlaying.value = false;
  animationTerminated.value = false;
};
defineExpose({ open });

const selectedId = ref<string>('');
const submit = (zip?: string) => {
  zip =
    zip ?? currCateItems.value.find((item) => item.id == selectedId.value)?.zip;
  visible.value = false;
  if (zip) _addWidgetTemplate(zip);
};

const afterClose = () => {
  selectedId.value = '';
  showControls.value = false;
  animationTerminated.value = true;
  if (requestAnimationFrameTimer.value)
    window.cancelAnimationFrame(requestAnimationFrameTimer.value);
};

const handleSelected = (selectedItem: WidgetItem) => {
  selectedId.value = selectedItem.id;
};
/**播放条是否显示 */
const showControls = ref<boolean>(false);
const handleMouseMove = (item: WidgetItem) => {
  if (item.name === '序列帧背景') {
    showControls.value = true;
    if (requestAnimationFrameTimer.value)
      window.cancelAnimationFrame(requestAnimationFrameTimer.value);
    requestAnimationFrameTimer.value = window.requestAnimationFrame(update);
  }
};
/**播放条进度 */
const framesSliderValue = ref<number>(0);
/**播放第几帧 */
const count = ref<number>(0);

const handleFramesChange = (val: number) => {
  count.value = Math.floor((val / 100) * (framesArr.value.length - 1));
  currCateItems.value.forEach((item) => {
    if (item.name == '序列帧背景')
      framePreview.value = framesArr.value[count.value];
  });
};

const sliderHover = ref(false);
const animationPlaying = ref(false);
const animationTerminated = ref(false);
/**序列帧自动按25帧/秒播放 */
const fps = ref(25);
const requestAnimationFrameTimer = ref(0);
let lastTime = 0;
const update = () => {
  if (animationTerminated.value) return;
  if (animationPlaying.value && !sliderHover.value) {
    const currentTime = new Date().getTime();
    const deltaTime = (currentTime - lastTime) / 1000;
    if (deltaTime >= 1 / fps.value) {
      currCateItems.value.forEach((item) => {
        if (item.name == '序列帧背景')
          framePreview.value =
            framesArr.value[count.value % framesArr.value.length];
      });
      count.value = count.value + 1;
      framesSliderValue.value =
        ((count.value % framesArr.value.length) / framesArr.value.length) * 100;
      lastTime = currentTime;
    }
  }
  if (requestAnimationFrameTimer.value)
    window.cancelAnimationFrame(requestAnimationFrameTimer.value);
  requestAnimationFrameTimer.value = window.requestAnimationFrame(update);
};

const handlePlayClick = () => {
  animationPlaying.value = !animationPlaying.value;
};
</script>

<style lang="less">
.modal-top {
  .ant-modal-content {
    background-color: #1f1f1f;
    border-radius: 5px;
    .ant-modal-header {
      border-radius: 5px 5px 0 0;
      background-color: #1f1f1f;
    }
    .ant-modal-body {
      background-color: #1f1f1f;
      padding: 0 0 0 30px;
    }
  }
}
</style>
<style lang="less" scoped>
:deep(.ant-slider) {
  .ant-slider-track {
    box-sizing: border-box;
    background-color: #515151;
  }
  .ant-slider-rail {
    border-radius: 5px;
    background-color: #515151;
  }
  .ant-slider-handle {
    width: 2px;
    height: 16px;
    border-radius: 3px;
    border: 2px solid #fff;
  }
  .ant-slider-dot-active {
    border-color: #fff;
  }
}
</style>
